<script setup lang="ts">
    import { computed, onMounted, watch } from 'vue'
    import { useStorage } from '@vueuse/core'
    import { SelectFieldSingle } from '@/js/components/Common'
    import { Fachbereich, Studiengang, ModulHandbuch } from '@/js/model'

    const props = withDefaults(
        defineProps<{
            studiengaenge: Studiengang[]
            fachbereiche: Fachbereich[]
            modulhandbuecher?: ModulHandbuch[]
        }>(),
        {
            modulhandbuecher: () => []
        }
    )

    const emit = defineEmits([
        'modulhandbuchSelected',
        'studiengangSelected',
        'fachbereichSelected',
        'userFilterSelected'
    ])

    function studiengangHasModulHandBuch(studiengang: Studiengang) {
        return (
            props.modulhandbuecher.filter(
                (modulhandbuch) => modulhandbuch.studiengangId === studiengang.studiengangId
            ).length > 0
        )
    }

    const fachbereichSelectOptions = computed(() => [
        { value: 'default', title: 'Fachbereich wählen ...', disabled: true },
        ...[...props.fachbereiche]
            .sort((a: Fachbereich, b: Fachbereich) => {
                const aId = a.hsaId ?? 0
                const bId = b.hsaId ?? 0

                if (aId < bId) return -1
                if (aId > bId) return 1
                return 0
            })
            .map((fachbereich) => {
                return {
                    title: `FB${fachbereich.hsaId} - ${fachbereich.kurzName}`,
                    value: fachbereich['@id']
                }
            })
    ])
    const studiengangSelectOptions = computed(() => [
        { value: 'default', title: 'Studiengang wählen ...', disabled: true },
        ...props.studiengaenge
            .filter((studiengang) => {
                return (
                    studiengangHasModulHandBuch(studiengang) &&
                    studiengang.fachbereich === selectedFachbereich.value
                )
            })
            .map((studiengang) => {
                return {
                    title: `${studiengang.kurzName} - ${studiengang.name}`,
                    value: studiengang.studiengangId
                }
            })
    ])
    const modulhandbuchSelectOptions = computed(() => [
        { value: 'default', title: 'Semester wählen ...', disabled: true },
        ...props.modulhandbuecher
            .filter((mhb) => mhb.studiengangId === selectedStudiengang.value)
            .map((mhb) => ({
                title: mhb.semester,
                value: mhb.modulHandbuchId
            }))
    ])
    const _storedFachbereich = useStorage('lernleitstand-fachbereich-select', 'default')
    const selectedFachbereich = computed({
        get: () =>
            fachbereichSelectOptions.value.some((o) => o.value === _storedFachbereich.value)
                ? _storedFachbereich.value
                : 'default',
        set: (val: string) => {
            _storedFachbereich.value = val
        }
    })

    const _storedStudiengang = useStorage('lernleitstand-studiengang-select', 'default')
    const selectedStudiengang = computed({
        get: () =>
            studiengangSelectOptions.value.some((o) => o.value === _storedStudiengang.value)
                ? _storedStudiengang.value
                : 'default',
        set: (val: string) => {
            _storedStudiengang.value = val
        }
    })
    const _storedModulhandbuch = useStorage('lernleitstand-modulhandbuch-select', 'default')
    const selectedSemester = computed({
        get: () =>
            modulhandbuchSelectOptions.value.some((o) => o.value === _storedModulhandbuch.value)
                ? _storedModulhandbuch.value
                : 'default',
        set: (val: string) => {
            _storedModulhandbuch.value = val
        }
    })
    const selectedUserFilter = useStorage('lernleitstand-filter-modul-user', 'alle')

    function getNextSemesterString(): string {
        const now = new Date()
        const month = now.getMonth() + 1
        const year = now.getFullYear()

        if (month >= 4 && month <= 9) {
            return `WiSe ${year}/${String(year + 1).slice(-2)}`
        } else if (month >= 10) {
            return `SoSe ${year + 1}`
        } else {
            return `SoSe ${year}`
        }
    }

    function semesterSortKey(title: string): number {
        const soSe = title.match(/^SoSe (\d{4})$/)
        if (soSe) return parseInt(soSe[1]) * 2

        const wiSe = title.match(/^WiSe (\d{4})\/\d{2}$/)
        if (wiSe) return parseInt(wiSe[1]) * 2 + 1

        return -1
    }

    function autoSelectNextSemester(options: typeof modulhandbuchSelectOptions.value) {
        const nextSemester = getNextSemesterString()
        const match =
            options.find((o) => o.title === nextSemester) ??
            [...options]
                .filter((o) => o.value !== 'default')
                .sort((a, b) => semesterSortKey(b.title) - semesterSortKey(a.title))[0]

        if (match && match.value !== 'default') {
            selectedSemester.value = match.value
            emit('modulhandbuchSelected', match.value)
        }
    }

    function handleSemesterChange(modulhandbuchId: string) {
        selectedSemester.value = modulhandbuchId
        emit('modulhandbuchSelected', modulhandbuchId)
    }

    watch(
        () => modulhandbuchSelectOptions.value,
        (options) => {
            if (_storedModulhandbuch.value !== 'default') return
            autoSelectNextSemester(options)
        },
        { immediate: true }
    )

    watch(
        () => selectedFachbereich.value,
        () => {
            emit('fachbereichSelected', selectedFachbereich.value)
            selectedStudiengang.value = 'default'
            selectedSemester.value = 'default'
        }
    )

    watch(
        () => selectedStudiengang.value,
        () => {
            emit('studiengangSelected', selectedStudiengang.value)
            selectedSemester.value = 'default'
            emit('modulhandbuchSelected', 'default')
            autoSelectNextSemester(modulhandbuchSelectOptions.value)
        }
    )
    watch(
        () => selectedUserFilter.value,
        () => {
            emit('userFilterSelected', selectedUserFilter.value)
        }
    )

    onMounted(() => {
        emit('fachbereichSelected', selectedFachbereich.value)
        emit('studiengangSelected', selectedStudiengang.value)
        emit('modulhandbuchSelected', selectedSemester.value)
        emit('userFilterSelected', selectedUserFilter.value)
    })
</script>

<template>
    <v-row dense>
        <v-col cols="12" sm="6" lg="3">
            <select-field-single
                v-model="selectedFachbereich"
                :items="fachbereichSelectOptions"
                label="Fachbereich"
                placeholder="Fachbereich wählen"
                data-test="select-fachbereich"
                class="ml-5"
            />
        </v-col>
        <v-col cols="12" sm="6" lg="3">
            <select-field-single
                v-model="selectedStudiengang"
                :items="studiengangSelectOptions"
                label="Studiengang"
                placeholder="Studiengang wählen"
                data-test="select-studiengang"
                class="ml-5"
            />
        </v-col>
        <v-col cols="12" sm="6" lg="2">
            <select-field-single
                v-model="selectedSemester"
                :items="modulhandbuchSelectOptions"
                label="Semester"
                placeholder="Semester wählen"
                data-test="select-semester"
                class="ml-5"
                @change="handleSemesterChange"
            />
        </v-col>
        <v-col cols="12" sm="6" lg="4" class="d-flex align-center flex-shrink-0">
            <v-btn-toggle
                v-model="selectedUserFilter"
                mandatory
                group
                tile
                class="ml-5 w-100 flex-shrink-0"
                style="min-width: max-content"
                color="primary"
                variant="outlined"
                density="compact"
            >
                <v-btn value="alle" data-test="radio-filter-module-user-off"> Alle Module </v-btn>
                <v-btn value="nurEigene" data-test="radio-filter-module-user-on">
                    Nur eigene Module
                </v-btn>
            </v-btn-toggle>
        </v-col>
    </v-row>
</template>

<script></script>

<style scoped></style>
